import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For beautiful results/benefits
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'; // For transformation/cosmetic
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // For durability/safety
import PaletteIcon from '@mui/icons-material/Palette'; // For custom design/aesthetics
import EditIcon from '@mui/icons-material/Edit'; // For smile design
import BuildIcon from '@mui/icons-material/Build'; // For preparation/fabrication
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for you?"
import BrokenImageIcon from '@mui/icons-material/BrokenImage'; // For chips/cracks
import ColorLensIcon from '@mui/icons-material/ColorLens'; // For stains
import ArtTrackIcon from '@mui/icons-material/ArtTrack'; // For artistry in "Why Choose Us"
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const PorcelainVeneersPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Achieve a dramatic and natural-looking smile transformation.',
    'Correct multiple cosmetic flaws with a single treatment.',
    'Crafted from high-quality porcelain for durability and longevity.',
    'Highly resistant to stains from coffee, tea, and wine.',
    'Requires minimal enamel removal compared to other restorations.',
    'Custom-designed to match your desired tooth shape, size, and color.'
  ];

  const veneerProcess = [
    {
      title: '1. Personalized Smile Design Consultation',
      description: 'Your journey begins with a detailed consultation at Pacifica Dental. We discuss your aesthetic goals, assess your oral health, and collaboratively design your dream smile, considering tooth shape, size, color, and overall facial harmony. We may use digital imaging or mock-ups to help you visualize the results.',
      icon: <EditIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Tooth Preparation & Impressions',
      description: 'A minimal amount of enamel (typically less than a millimeter) is gently removed from the front surface of the teeth to create space for the veneers. We then take precise impressions of your teeth, which are sent to a high-quality dental laboratory.',
      icon: <BuildIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '3. Custom Veneer Fabrication & Temporaries',
      description: 'Skilled technicians at the dental lab will meticulously craft your custom porcelain veneers based on our design and your impressions. While your permanent veneers are being made, we may place temporary veneers to protect your teeth.',
      icon: <PaletteIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Veneer Bonding & Final Adjustments',
      description: 'Once your veneers are ready, you\'ll return to Pacifica Dental. We\'ll check the fit, shape, and color before permanently bonding them to your teeth using a strong dental adhesive. Final adjustments are made to ensure a perfect bite and a stunning, comfortable smile.',
      icon: <CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const concernsAddressed = [
    'Stubborn stains or discoloration unresponsive to whitening.',
    'Chipped, cracked, or broken teeth.',
    'Gaps or spaces between teeth.',
    'Minor misalignment or crooked teeth.',
    'Unevenly sized or shaped teeth.',
    'Worn down enamel.'
  ];

  const isRightForYou = [
    'You are in good overall oral health, with healthy gums and no untreated decay.',
    'You have specific cosmetic concerns that veneers can effectively address.',
    'You are committed to maintaining excellent oral hygiene.',
    'You understand that porcelain veneers are a permanent cosmetic enhancement.',
    'You have realistic expectations for the outcome of your smile makeover.',
    'You do not have severe teeth grinding habits (bruxism) or are willing to wear a nightguard.'
  ];

  const caringForVeneers = [
    'Practice excellent oral hygiene: Brush gently twice a day and floss daily.',
    'Use a non-abrasive fluoride toothpaste.',
    'Avoid biting into very hard objects (ice, nuts, pens) or using your teeth as tools.',
    'Attend regular dental check-ups and cleanings at Pacifica Dental.',
    'Wear a custom nightguard if you clench or grind your teeth.',
    'Report any issues like chipping or loosening to us promptly.'
  ];
  
  const faqs = [
    {
      question: 'How long do porcelain veneers last?',
      answer: 'With proper care and regular dental check-ups at Pacifica Dental, porcelain veneers can last 10-15 years or even longer. Their longevity depends on your oral hygiene and lifestyle habits.'
    },
    {
      question: 'Is the porcelain veneer procedure painful?',
      answer: 'The procedure is generally very comfortable. Local anesthesia is used during the tooth preparation stage to ensure you don\'t feel any discomfort. Some patients may experience temporary sensitivity afterward, which is manageable.'
    },
    {
      question: 'How much tooth enamel needs to be removed for veneers?',
      answer: 'A very thin layer of enamel, usually less than a millimeter (similar to the thickness of a contact lens), is removed to accommodate the veneer. This makes the procedure minimally invasive compared to other options like crowns.'
    },
    {
        question: 'Can porcelain veneers stain or chip?',
        answer: 'Porcelain is highly resistant to staining. However, veneers can chip or break under excessive force, similar to natural teeth. Avoiding bad habits like biting nails or hard objects is important.'
    },
    {
        question: 'Are there alternatives to porcelain veneers?',
        answer: 'Depending on your specific concerns, alternatives might include dental bonding (for minor chips or gaps), teeth whitening (for discoloration), or orthodontic treatment (for more significant misalignment). We will discuss all suitable options with you at Pacifica Dental.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Porcelain Veneers | Pacifica Dental | Smile Makeover Experts</title>
        <meta name="description" content="Transform your smile with custom porcelain veneers at Pacifica Dental. Correct stains, chips, gaps, and more for a stunning, natural look. Call (408) 869-0998." />
        <meta name="keywords" content="porcelain veneers Pacifica Dental, dental veneers, cosmetic dentistry Pacifica Dental, smile makeover, teeth veneers, (408) 869-0998, San Jose CA veneers, Bay Area cosmetic dentist" /> {/* Adjusted local keywords */}
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Craft Your Dream Smile with Porcelain Veneers at Pacifica Dental
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Achieve a Flawless, Natural-Looking Smile Transformation
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              Porcelain veneers are one of the most versatile and transformative treatments in cosmetic dentistry. At Pacifica Dental, we expertly design and place these ultra-thin, custom-made shells of dental porcelain to conceal imperfections and create a stunning, symmetrical, and radiant smile. If you dream of correcting chips, stains, gaps, or misalignments, our porcelain veneers can provide a durable and aesthetically superior solution.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    The Advantages of Choosing Porcelain Veneers
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <StarIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Veneer Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Your Porcelain Veneer Journey at Pacifica Dental
                  </Typography>
                  {veneerProcess.map((step, index) => (
                    <Box key={index} sx={{ mb: index === veneerProcess.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}> {/* alignItems to flex-start if text is long */}
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* What Concerns Can Veneers Address? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                What Can Porcelain Veneers Correct?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Porcelain veneers are a versatile solution for a wide range of cosmetic dental imperfections, including:
                </Typography>
                <Grid container spacing={1}>
                    {concernsAddressed.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        {index % 3 === 0 ? <ColorLensIcon /> : index % 3 === 1 ? <BrokenImageIcon /> : <AutoFixHighIcon />}
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Is It Right For You? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Are Porcelain Veneers Your Ideal Smile Solution?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Personalized Consultation is Key</AlertTitle>
                A thorough consultation at Pacifica Dental is essential to determine if porcelain veneers are the best option for your smile goals and dental health. We'll explore all possibilities with you.
              </Alert>
              <Grid container spacing={2}>
                {isRightForYou.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <CheckCircleOutlineIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring For Your Veneers Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Maintaining the Beauty of Your Porcelain Veneers
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {caringForVeneers.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <VerifiedUserIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Porcelain Veneer FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Porcelain Veneers?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Cosmetic Dentistry Expertise", text: "Our dentists at Pacifica Dental possess advanced training and an artistic eye for creating beautiful, natural-looking smile makeovers with veneers.", icon: <ArtTrackIcon color="primary" /> },
                      { title: "Personalized Smile Design", text: "We take a highly customized approach, collaborating with you to design veneers that perfectly complement your facial features and aesthetic desires.", icon: <PaletteIcon color="primary" /> },
                      { title: "Premium Quality Materials & Labs", text: "We partner with top-tier dental laboratories and use the highest quality porcelain for durable, lifelike, and stunning results.", icon: <VerifiedUserIcon color="primary" /> },
                      { title: "Commitment to Patient Satisfaction", text: "Your happiness with your new smile is our ultimate goal. We ensure meticulous attention to detail and provide a comfortable, supportive experience.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready to Unveil Your Perfect Smile?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Discover how porcelain veneers can transform your confidence. Contact Pacifica Dental today to schedule your personalized smile consultation!
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Veneers Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to discuss your smile goals.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default PorcelainVeneersPacificaDental;